import { AnalyticsPlugin } from 'analytics';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';

import { TrackingPixelFiresOn } from '../enums/TrackingPixelFiresOn';
import { TrackingPixelVendor } from '../enums/TrackingPixelVendor';
import { createTrackingPixelPlugin } from '../functions/createTrackingPixelPlugin';
import { ensureVendorScript } from '../functions/ensureVendorScript';
import { YahooDotQueueItem } from '../types/YahooDotQueueItem';
import { YahooDotWindow } from '../types/YahooDotWindow';

const YTC_SRC = 'https://s.yimg.com/wi/ytc.js';

const loadYtc = (yahooWindow: YahooDotWindow): void => {
  yahooWindow.dotq = yahooWindow.dotq || [];

  // ytc.js sends no beacons by itself: Yahoo's snippet drains the queue on
  // load, then makes every later push fire straight away
  ensureVendorScript(YTC_SRC, (): void => {
    try {
      const { fireBeacon } = yahooWindow.YAHOO.ywa.I13N;
      const queued = yahooWindow.dotq;

      yahooWindow.dotq = [];
      yahooWindow.dotq.push = (...items: YahooDotQueueItem[]): number => {
        fireBeacon(items);

        return 0;
      };

      fireBeacon(queued);
    } catch {
      // as in Yahoo's snippet: a ytc.js that failed to set up sends nothing
    }
  });
};

const toQstrings = (
  event: string,
  eventName: string,
  properties: Record<string, unknown> = {},
): Record<string, unknown> => ({
  ...(event === TrackingPixelFiresOn.PAGE_VIEW
    ? {}
    : omitBy(properties, isNil)),
  ea: eventName,
  et: 'custom',
});

export const yahooDotPlugin = (): AnalyticsPlugin =>
  createTrackingPixelPlugin(
    TrackingPixelVendor.YAHOO_DOT,
    (pixels, { event, properties }): void => {
      const yahooWindow = window as YahooDotWindow;

      loadYtc(yahooWindow);

      pixels.forEach(({ accountId, eventName, pixelIds }): void => {
        pixelIds.forEach((pixelId): void => {
          yahooWindow.dotq.push({
            projectId: accountId,
            properties: eventName
              ? { pixelId, qstrings: toQstrings(event, eventName, properties) }
              : { he: '', hph: '', pixelId },
          });
        });
      });
    },
  );
